/**
 * Webhooks — developer mode surface.
 *
 * Endpoint URL + signing secret (reveal/rotate), delivery log, manual
 * retry for dead deliveries, verification snippet.
 */

import { useEffect, useState } from "react";
import {
  ApiError,
  getWebhookEndpoint,
  listWebhookDeliveries,
  retryWebhookDelivery,
  setWebhookEndpoint,
  type WebhookDelivery,
  type WebhookEndpoint,
} from "../lib/api";
import { Banner, Btn, KV } from "../components/ui";
import { Ic } from "../components/Icons";
import { getStoredApiKey } from "../components/RawKeyModal";

const STATUS_PILL: Record<WebhookDelivery["status"], string> = {
  pending: "pill warn",
  delivered: "pill ok",
  dead: "pill fail",
  manual_retry: "pill muted",
};

const formatRelative = (t: number): string => {
  const delta = Date.now() - t;
  const min = 60_000;
  const hour = 3_600_000;
  const day = 86_400_000;
  if (delta < min) return "just now";
  if (delta < hour) return `${Math.floor(delta / min)}m ago`;
  if (delta < day) return `${Math.floor(delta / hour)}h ago`;
  return `${Math.floor(delta / day)}d ago`;
};

export function Webhooks() {
  const [endpoint, setEndpointState] = useState<WebhookEndpoint | null>(null);
  const [deliveries, setDeliveries] = useState<WebhookDelivery[] | null>(null);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);
  const apiKey = getStoredApiKey();

  const refresh = async () => {
    if (!apiKey) return;
    try {
      const [ep, dl] = await Promise.all([
        getWebhookEndpoint(apiKey),
        listWebhookDeliveries(apiKey),
      ]);
      setEndpointState(ep);
      setUrl(ep.url ?? "");
      setDeliveries(dl.data);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey]);

  if (!apiKey) {
    return (
      <div className="scroll">
        <div className="pad">
          <Banner tone="warn">
            Open the <strong>API keys</strong> tab and cache (or mint) a key —
            the dashboard needs it to read webhook config and deliveries.
          </Banner>
        </div>
      </div>
    );
  }

  const handleSave = async (rotate: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const r = await setWebhookEndpoint(apiKey, { url, rotate });
      if (r.signing_secret) setRevealedSecret(r.signing_secret);
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const handleRetry = async (id: number) => {
    try {
      await retryWebhookDelivery(apiKey, id);
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  return (
    <div className="scroll">
      <div className="pad fade-in">
        {error && (
          <div style={{ marginBottom: 12 }}>
            <Banner tone="fail">{error}</Banner>
          </div>
        )}

        <div className="s-label">Endpoint</div>
        <div className="card card-pad">
          <div className="field">
            <label htmlFor="url">Webhook URL (https only)</label>
            <input
              id="url"
              className="input"
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://your-site.example/webhooks/shieldz"
            />
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <Btn
              onClick={() => void handleSave(false)}
              disabled={busy || !url}
            >
              {endpoint?.url ? "Save URL" : "Save"}
            </Btn>
            {endpoint?.url && (
              <Btn
                variant="secondary"
                onClick={() => void handleSave(true)}
                disabled={busy}
              >
                Save + rotate secret
              </Btn>
            )}
          </div>
          {endpoint?.signing_secret_prefix && !revealedSecret && (
            <div className="help" style={{ marginTop: 12 }}>
              Signing secret: <code className="mono">{endpoint.signing_secret_prefix}…</code>{" "}
              · Rotate to reveal a new value.
            </div>
          )}
          {revealedSecret && (
            <div style={{ marginTop: 12 }}>
              <Banner tone="warn">
                Save this signing secret now — we won't show it again:
              </Banner>
              <pre
                className="mono"
                style={{
                  marginTop: 8,
                  padding: 10,
                  background: "var(--surface-2)",
                  border: "1px solid var(--line)",
                  borderRadius: 8,
                  fontSize: 11,
                  overflow: "auto",
                }}
              >
                {revealedSecret}
              </pre>
            </div>
          )}
        </div>

        <div className="s-label">Recent deliveries</div>
        <div className="card">
          {deliveries === null ? (
            <div className="card-pad" style={{ color: "var(--ink-3)" }}>
              Loading…
            </div>
          ) : deliveries.length === 0 ? (
            <div className="empty">
              <h4>No deliveries yet</h4>
              <p>
                These appear the first time an invoice transitions to paid,
                failed, or expired.
              </p>
            </div>
          ) : (
            deliveries.map((d) => (
              <div key={d.id} className="li">
                <div className="li-main">
                  <div style={{ fontSize: 13.5, fontWeight: 500 }}>
                    {d.event_type}
                  </div>
                  <div className="li-meta">
                    attempt {d.attempt} · {formatRelative(d.created_at)}
                    {d.http_status_code != null ? ` · HTTP ${d.http_status_code}` : ""}
                  </div>
                </div>
                <div className="li-right">
                  <span className={STATUS_PILL[d.status]}>
                    <span className="pd" />
                    {d.status}
                  </span>
                  {d.status === "dead" && (
                    <Btn
                      variant="ghost"
                      onClick={() => void handleRetry(d.id)}
                      style={{ padding: "4px 10px" }}
                    >
                      Retry
                    </Btn>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="s-label">Verification (Node)</div>
        <div className="card card-pad">
          <pre
            className="mono"
            style={{
              margin: 0,
              padding: 10,
              background: "var(--surface-2)",
              border: "1px solid var(--line)",
              borderRadius: 8,
              fontSize: 11,
              overflow: "auto",
            }}
          >
{`function verifyShieldzWebhook(rawBody, sigHeader, secret) {
  const m = /^t=(\\d+),v1=([0-9a-f]+)/.exec(sigHeader);
  if (!m) throw new Error("malformed signature");
  const [, t, v1] = m;
  if (Math.abs(Date.now()/1000 - +t) > 300) throw new Error("stale");
  const expected = require("crypto")
    .createHmac("sha256", secret)
    .update(\`\${t}.\${rawBody}\`)
    .digest("hex");
  if (v1 !== expected) throw new Error("bad signature");
}`}
          </pre>
          <div className="help" style={{ marginTop: 10 }}>
            Idempotency-key your handler by the <code className="mono">X-Shieldz-Delivery</code>{" "}
            header — same delivery may retry across a 14-hour window. During
            the 24h after rotation, the header carries multiple <code className="mono">v1=</code>{" "}
            values; accept on any match.
          </div>
        </div>
      </div>
    </div>
  );
}
